import { gql } from '@apollo/client';

export const MULTIMEDIA_FRAGMENT = gql`
  fragment MultimediaFragment on Multimedia {
    _id
    src
    alt
    type
  }
`;

export const PHONE_FRAGMENT = gql`
  fragment PhoneFragment on Phone {
    _id
    code
    phone
    active
  }
`;

export const LOCATION_FRAGMENT = gql`
  fragment LocationFragment on Location {
    name
    description
    photos {
      ...MultimediaFragment
    }
    address {
      slug
      country
      province
      municipality
      parish
      city
      zip
      address1
      coordinates {
        type
        coordinates
        _id
      }
      extra
      active
      _id
    }
    active
    _id
  }
  ${MULTIMEDIA_FRAGMENT}
`;

export const SPECS_FRAGMENT = gql`
  fragment SpecsFragment on Specs {
    _id
    coolantColor
    oilFilter
    oilOrigin
    oilViscosity
    psi
    screenWasher
    tireFiller
    oilQuantity
  }
`;

export const VEHICLE_FRAGMENT = gql`
  fragment VehicleFragment on Vehicle {
    _id
    brand {
      _id
      name
    }
    model {
      _id
      name
    }
    version {
      _id
      engine {
        transmission
        volume
        cylinders
        valves
      }
      year
    }
    plate
    engineSerial
    vin
    color
    note
    owner
    active
    token
    specs {
      ...SpecsFragment
    }
  }
  ${SPECS_FRAGMENT}
`;

export const DRIVER_FRAGMENT = gql`
  fragment driverFragment on Driver {
    _id
    name
    dni
    dniType
    email
    phone {
      ...PhoneFragment
    }
    active
  }
  ${PHONE_FRAGMENT}
`;

export const CATEGORY_FRAGMENT = gql`
  fragment CategoryFragment on Category {
    title
    slug
    description
    icon
    active
    _id
    #subcategory
  }
`;

export const VARIANT_FRAGMENT = gql`
  fragment VariantFragment on Variant {
    _id
    title
    barcode
    cost
    barcode
    price
    compareAtPrice
    stock
    minStock
    maxStock
    sku
    option {
      option1
      option2
      option3
    }
    photo {
      ...MultimediaFragment
    }
    location {
      ...LocationFragment
    }
  }
`;

export const PRODUCT_FRAGMENT = gql`
  fragment ProductFragment on Product {
    _id
    title
    brand
    tags
    slug
    description
    priority
    placeToSell
    status
    categories {
      ...CategoryFragment
    }
    photos {
      ...MultimediaFragment
    }
    variants {
      ...VariantFragment
    }
    options {
      name
      values
      active
      _id
    }
    taxes {
      name
      value
      active
    }
    #rating
    #points
    #reviews
    #volatileInventory
    #extraInfo
    #comments
    #likes
    #history
  }
  ${MULTIMEDIA_FRAGMENT}
  ${LOCATION_FRAGMENT}
  ${CATEGORY_FRAGMENT}
  ${VARIANT_FRAGMENT}
`;

export const ORDER_PRODUCT_FRAGMENT = gql`
  fragment OrderProductFragment on OrderProduct {
    _id
    title
    cost
    discount
    quantity
    price
    option {
      _id
      option1
      option2
      option3
    }
    product {
      ...ProductFragment
    }
    photo {
      ...MultimediaFragment
    }
    location {
      ...LocationFragment
    }
    variant {
      ...VariantFragment
    }
    taxes {
      _id
      value
      tax
    }
  }
  ${MULTIMEDIA_FRAGMENT}
  ${LOCATION_FRAGMENT}
  ${VARIANT_FRAGMENT}
  ${PRODUCT_FRAGMENT}
`;

export const SHOPCART_FRAGMENT = gql`
  fragment ShopcartFragment on Shopcart {
    _id
    client
    status
    items {
      ...OrderProductFragment
    }
  }

  ${PRODUCT_FRAGMENT}
  ${ORDER_PRODUCT_FRAGMENT}
`;

export const CLIENT_FRAGMENT = gql`
  fragment ClientFragment on Client {
    _id
    acceptsMarketing
    user
    notes
    points
    birthdate
    #promos
    vehicles {
      ...VehicleFragment
    }
    #addresses
    shopCart {
      ...ShopcartFragment
    }
  }
  ${VEHICLE_FRAGMENT}
  ${SHOPCART_FRAGMENT}
`;

export const USER_FRAGMENT = gql`
  fragment UserFragment on User {
    _id
    firstName
    lastName
    email
    dni
    dniType
    userType
    client {
      ...ClientFragment
    }
    photo {
      ...MultimediaFragment
    }
    phone {
      ...PhoneFragment
    }
  }
  ${MULTIMEDIA_FRAGMENT}
  ${PHONE_FRAGMENT}
  ${CLIENT_FRAGMENT}
`;

export const PAGINATION_FRAGMENT = gql`
  fragment PaginationFragment on PageInfoAppointment {
    currentPage
    perPage
    pageCount
    itemCount
    hasNextPage
    hasPreviousPage
  }
`;

export const ORDER_FRAGMENT = gql`
  fragment OrderFragment on Order {
    _id
    status
    paymentStatus
    user {
      ...UserFragment
    }
    subtotal
    extraFee
    discountTotal
    taxBase
    taxTotal
    total
    gatewayCommission
    promoCodes
    billingAddress
    note
    discounts {
      _id
      discount
      value
    }
    history {
      _id
      user
      actionType
      comment
    }
    paymentInfo {
      _id
      gateway
      amount
      currency
      rate
      metadata
    }
    items {
      ...OrderProductFragment
    }
  }
  ${ORDER_PRODUCT_FRAGMENT}
  ${USER_FRAGMENT}
`;
export const APPOINTMENT_FRAGMENT = gql`
  fragment AppointmentFragment on Appointment {
    _id
    date
    active
    reservationNumber
    status
    services {
      name
      order
      active
    }
    order {
      ...OrderFragment
    }
    location {
      ...LocationFragment
    }
    vehicle {
      ...VehicleFragment
    }
    driver {
      ...driverFragment
    }
    user {
      _id
      email
    }
  }
  ${VEHICLE_FRAGMENT}
  ${DRIVER_FRAGMENT}
  ${LOCATION_FRAGMENT}
  ${ORDER_FRAGMENT}
`;

export const DISCOUNT_FRAGMENT = gql`
  fragment DiscountFragment on Discount {
    _id
    name
    code
    value
    photo {
      ...MultimediaFragment
    }
    type
    date
    category {
      title
    }
    url
    active
  }
  ${MULTIMEDIA_FRAGMENT}
`;

export const CURRENCY_FRAGMENT = gql`
  fragment CurrencyFragment on Currency {
    _id
    name
    symbol
    rate
    localCoin
    active
  }
`;

export const REPAIR_FRAGMENT = gql`
  fragment RepairFragment on Repair {
    _id
    vehicle
    mechanic
    order
    start
    finish
    description
    process {
      _id
      step
      title
      stepInstruction {
        instruction
        description
        photo {
          ...MultimediaFragment
        }
      }
      date
      issue
      issueStatus
      resultStatus
      result
      resultOptions
    }
    items
    extraFee
    status
    photo {
      ...MultimediaFragment
    }
    subtotal
    discount
    taxes
    total
    currentStep
    active
    createdAt
    updatedAt
  }
  ${MULTIMEDIA_FRAGMENT}
`;
